import React, { Component } from 'react';
import { PropTypes as PT } from 'prop-types';
import { defineMessages, injectIntl, FormattedMessage, IntlProvider } from 'react-intl';
import { Dropdown } from 'semantic-ui-react';
import ReactSpinner from 'react16-spinjs';
import { BrowserRouter as Router } from 'react-router-dom';
import _find from 'lodash/find';
import lsLogo from './assets/images/ls_logo_horizontal_white.svg';
import browserInfo, { helpUrl } from './utils/browserUtils';
import { getDevices } from './utils/deviceUtils';
import { getAdminSettings } from './utils/adminUtils';
import DeviceList from './components/DeviceList';
import Unsupported from './components/Unsupported';
import SelectLocale from './components/SelectLocale';
import FailurePage from './components/FailurePage';
import Notification from './components/Notification';
import RegisterFailedNotification from './components/RegisterFailedNotification';
import Modal, { SETTINGS_MODAL, MODIFY_MODAL } from './components/Modal';
import './App.scss';

import csJson from './assets/locale/cs.json';
import deJson from './assets/locale/de.json';
import enJson from './assets/locale/en.json';
import esJson from './assets/locale/es.json';
import fiJson from './assets/locale/fi.json';
import frJson from './assets/locale/fr.json';
import itJson from './assets/locale/it.json';
import jaJson from './assets/locale/ja.json';
import koJson from './assets/locale/ko.json';
import nlJson from './assets/locale/nl.json';
import noJson from './assets/locale/no.json';
import plJson from './assets/locale/pl.json';
import ruJson from './assets/locale/ru.json';
import ptJson from './assets/locale/pt.json';
import svJson from './assets/locale/sv.json';
import zhJson from './assets/locale/zh_Hans.json';
import zhTwJson from './assets/locale/zh_Hant.json';

const TERMS_OF_SERVICE_LINK = 'https://www.lifesize.com/~/media/Documents/Other%20Documents/Legal%20Compliance/End%20User%20License%20Agreements/Lifesize%20Cloud%20Terms%20of%20Service%20TOS.ashx?la=en';
const PRIVACY_LINK = 'https://www.lifesize.com/en/company/legal-notices/privacy';
export const ALREADY_REGISTERED = 'ALREADY_REGISTERED';
export const ANOTHER_REGISTERED = 'ANOTHER_REGISTERED';
export const GENERIC_REGISTERED = 'GENERIC_REGISTERED';

const locales = {
  cs: csJson,
  de: deJson,
  en: enJson,
  es: esJson,
  fi: fiJson,
  fr: frJson,
  it: itJson,
  ja: jaJson,
  nl: nlJson,
  no: noJson,
  pl: plJson,
  ru: ruJson,
  pt: ptJson,
  sv: svJson,
  zh: zhJson,
  zh_Hant: zhTwJson,
  ko: koJson
};

const spinnerConfig = {
  lines: 13,
  length: 37,
  width: 19,
  radius: 47,
  color: '#413675',
  speed: 0.7
};

const messages = defineMessages({
  lifesize: {
    id: 'lifesize',
    defaultMessage: 'Lifesize'
  },
  adminLogout: {
    id: 'logout',
    defaultMessage: 'Admin logout'
  },
  adminLogin: {
    id: 'login',
    defaultMessage: 'Admin login'
  },
  privacyPolicy: {
    id: 'privacyPolicy',
    defaultMessage: 'Privacy Policy'
  },
  termsOfService: {
    id: 'termsOfService',
    defaultMessage: 'Terms of Service'
  }
});

const logoComp = props => (
  <div className="lsLogoContainer">
    <img
      src={lsLogo}
      className="lsLogo"
      alt={props.intl.formatMessage(messages.lifesize)}
    />
  </div>
);

logoComp.propTypes = {
  intl: PT.object
};

const footerComp = (props) => {
  if (browserInfo.isMobile) {
    return (
      <footer className="footer mobileFooter">
        <div className="mobileFooterLine">
          <FormattedMessage
            id="footerMessagePartOne"
            defaultMessage="© {year} Lifesize, Inc. All rights reserved."
            values={{
              link: <a href="http://www.lifesize.com">lifesize.com</a>,
              year: new Date().getFullYear()
            }}
          />
        </div>
        <div className="mobileFooterLine">
          <FormattedMessage
            id="footerMessagePartTwo"
            defaultMessage="{privacyPolicy} | {termsOfService}"
            values={{
              privacyPolicy:
    <a href={PRIVACY_LINK}>{props.intl.formatMessage(messages.privacyPolicy)}</a>,
              termsOfService:
    <a href={TERMS_OF_SERVICE_LINK}>{props.intl.formatMessage(messages.termsOfService)}</a>
            }}
          />
        </div>
      </footer>
    );
  }

  return (
    <footer className="footer">
      <FormattedMessage
        id="footerMessage"
        defaultMessage="© {year} Lifesize, Inc. All rights reserved. {privacyPolicy} | {termsOfService}"
        values={{
          link: <a href="http://www.lifesize.com">lifesize.com</a>,
          year: new Date().getFullYear(),
          privacyPolicy:
    <a href={PRIVACY_LINK}>{props.intl.formatMessage(messages.privacyPolicy)}</a>,
          termsOfService:
    <a href={TERMS_OF_SERVICE_LINK}>{props.intl.formatMessage(messages.termsOfService)}</a>
        }}
      />
    </footer>
  );
};

footerComp.propTypes = {
  intl: PT.object
};

const Logo = injectIntl(logoComp);
const Footer = injectIntl(footerComp);
let isMounted;

class App extends Component {
  constructor(props) {
    super(props);
    let registerState;
    let registerFailed = null;

    const errorNode = document.getElementById('error_state');
    const setupNode = document.getElementById('setup_state');
    const registerNode = document.getElementById('register_state');

    if (registerNode) {
      registerState = registerNode.getAttribute('data-attr');
      if (registerState === 'already_registered') {
        registerState = null;
        registerFailed = { error: GENERIC_REGISTERED };
      }
    }

    let setupState;
    if (window.location.pathname === '/change_setup') {
      setupState = true;
    } else {
      setupState = setupNode ? setupNode.getAttribute('data-attr') : null;
    }

    this.state = {
      locale: localStorage.getItem('locale') || 'en',
      contacts: [],
      isadmin: registerNode ? registerNode.getAttribute('data-attr') : false,
      contactsLoading: false,
      errorState: errorNode ? errorNode.getAttribute('data-attr') : null,
      setupState,
      registerState,
      notification: null,
      modal: null,
      loadingAdminSettings: true,
      adminSettings: null,
      registerFailed,
      scrollToContact: null,
      notificationTimeout: null
    };

    this.errorState = new MutationObserver((mutation) => {
      if (isMounted) {
        this.setState({ errorState: mutation[0].target.getAttribute('data-attr') });
      }
    });
  }

  componentWillMount() {
    this.setState({ contactsLoading: true });
  }

  componentDidMount() {
    isMounted = true;

    getDevices().then((results) => {
      if (isMounted) {
        this.setState(Object.assign({}, { contactsLoading: false }, results));
      }
    })
    .catch(e => (e));

    if (this.errorState && this.errorState.observe) {
      this.errorState.observe(document.getElementById('error_state'), { attributes: true });
    }

    getAdminSettings().then((results) => {
      if (isMounted) {
        this.setState({ adminSettings: results, loadingAdminSettings: false });
      }
    })
    .catch(() => {
      if (isMounted) {
        this.setState({ loadingAdminSettings: false });
      }
    });
  }

  componentWillUnmount() {
    isMounted = false;
    if (this.errorState && this.errorState.disconnect) {
      this.errorState.disconnect();
    }
  }

  setNotification = (message, timeout) => {
    this.setState({ notification: message, notificationTimeout: timeout });
  };

  setModal = (type, props) => {
    if (!type) {
      this.setState({ modal: null });
    } else {
      this.setState({ modal: { type, props } });
    }
  };

  changeLocale = (locale) => {
    localStorage.setItem('locale', locale);
    this.setState({ locale });
  };

  removeDeviceFromList = (serial) => {
    this.setState({ contacts: this.state.contacts.filter(device => (device.serial !== serial)) });
  };

  clearError = () => {
    document.getElementById('error_state').setAttribute('data-attr', '');
  };

  updateContact = (contact) => {
    this.setState({ contactsLoading: true });
    this.setState({ registerState: null });
    if (_find(this.state.contacts,
          registeredContact => registeredContact.serial === contact.serial)) {
      this.setState({ registerFailed: { reason: ALREADY_REGISTERED } });
    }
    getDevices().then((results) => {
      if (isMounted) {
        this.setState(Object.assign({}, { contactsLoading: false }, results));
        if (!_find(this.state.contacts,
              registeredContact => registeredContact.serial === contact.serial)) {
          this.setState({ registerFailed: { reason: ANOTHER_REGISTERED } });
        } else {
          this.setState({ scrollToContact: contact });
        }
      }
    })
    .catch(e => (e));
  };

  loginDropdownComp = ({ intl }) => {
    const dropdownOptions = this.state.isadmin ?
        [{ text: intl.formatMessage(messages.adminLogout) }] :
        [{ text: intl.formatMessage(messages.adminLogin) }];

    return (
      <Dropdown
        className="icon icon-avatar loginDropdown"
        options={dropdownOptions}
        text=""
        onChange={() => {
          if (this.state.isadmin) {
            window.location = '/logout';
          } else {
            window.location = '/login';
          }
        }}
      />
    );
  };

  renderBody = () => {
    if (browserInfo.IE && browserInfo.detectedVersion < 11) {
      return <Unsupported />;
    }

    if (this.state.errorState) {
      return (
        <FailurePage
          errorState={this.state.errorState}
          clearError={this.clearError}
        />
      );
    }


    if (this.state.setupState && !this.state.loadingAdminSettings) {
      return (
        <div className="modalWrapper">
          <Modal
            type={SETTINGS_MODAL}
            firstTime={window.location.pathname !== '/change_setup'}
            {...this.state.adminSettings}
          />
        </div>
      );
    }

    if (this.state.contactsLoading) {
      return <ReactSpinner config={spinnerConfig} />;
    }

    if (this.state.registerState) {
      return (
        <div className="modalWrapper">
          <Modal
            type={MODIFY_MODAL}
            firstTime
            contact={{
              serial: this.state.registerState
            }}
            updateContact={this.updateContact}
            setModal={(type, props) => {
              this.setState({ registerState: null });
              this.setModal(type, props);
            }}
          />
        </div>
      );
    }

    return (
      <DeviceList
        contacts={this.state.contacts}
        isadmin={this.state.isadmin}
        removeDeviceFromList={this.removeDeviceFromList}
        setNotification={this.setNotification}
        scrollToContact={this.state.scrollToContact}
      />);
  };

  render() {
    const { locale } = this.state;
    const LoginDropdown = injectIntl(this.loginDropdownComp);

    return (
      <IntlProvider
        key={locale}
        locale={locale}
        messages={locales[locale] || locales.en}
      >
        <Router>
          <div className="container">
            <div className="header">
              <Logo />
              {this.state.isadmin && !this.state.loadingAdminSettings &&
                <button
                  className="adminSettings"
                  onClick={() => {
                    if (!this.state.modal || !this.state.modal.type !== SETTINGS_MODAL) {
                      this.setModal(SETTINGS_MODAL, this.state.adminSettings);
                    }
                  }}
                >
                  <FormattedMessage
                    id="adminSettings"
                    defaultMessage="Admin Settings"
                  />
                </button>
              }
              <button
                className="icon icon-walkme walkme"
                onClick={() => {
                  window.open(helpUrl(locale));
                }}
              />
              <div className="languageDropdown">
                <SelectLocale
                  changeLocale={this.changeLocale}
                />
              </div>
              {!this.state.contactsLoading &&
                <LoginDropdown />}
            </div>
            {this.state.notification &&
              <Notification
                content={this.state.notification}
                setNotification={this.setNotification}
                timeout={this.state.notificationTimeout}
                autoHide
              />
            }
            <div className="body" id="body">
              {this.state.modal &&
              <div className="modalWrapper">
                <Modal
                  setModal={this.setModal}
                  type={this.state.modal.type}
                  {...this.state.modal.props}
                />
              </div>}
              {this.state.registerFailed &&
                <RegisterFailedNotification
                  setNotification={this.setNotification}
                  removeRegistration={() => {
                    this.setState({ registerFailed: null });
                  }}
                  error={this.state.registerFailed.reason}
                />
              }
              {this.renderBody()}
            </div>
            <Footer />
          </div>
        </Router>
      </IntlProvider>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js